import api, { executeQuery } from '../utils/api';
import { Stock } from '../types';
import { getStoreId, findStoreWithProduct, getAllAvailableStores } from '../utils/storeUtilsClient';

/**
 * 从 executeQuery 响应中安全地提取数据数组
 * executeQuery 可能返回 {success, data} 或直接返回数据
 */
const extractData = (response: any): any[] => {
  if (!response) return [];
  // 如果是包装对象，提取 data 属性
  const data = response?.data || response;
  // 确保返回的是数组
  return Array.isArray(data) ? data : [];
};

const STOCK_DEBUG =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_STOCK === 'true';
const stockLog = (..._args: any[]) => {
  void STOCK_DEBUG;
};
const stockWarn = (..._args: any[]) => {
  void STOCK_DEBUG;
};

const normalizeStockLimit = (preferredValue: any, fallbackValue: any, defaultValue = 0): number => {
  const preferred = Number(preferredValue);
  if (preferredValue !== null && preferredValue !== undefined && Number.isFinite(preferred)) {
    return preferred;
  }

  const fallback = Number(fallbackValue);
  if (fallbackValue !== null && fallbackValue !== undefined && Number.isFinite(fallback)) {
    return fallback;
  }

  return defaultValue;
};

const applyArticuloStockLimits = <T extends Record<string, any>>(row: T | undefined): T | undefined => {
  if (!row) return row;

  const normalizedRow = row as Record<string, any>;
  normalizedRow['MiniStock'] = normalizeStockLimit(normalizedRow['articulo_MiniStock'], normalizedRow['MiniStock']);
  normalizedRow['MaxiStock'] = normalizeStockLimit(normalizedRow['articulo_MaxiStock'], normalizedRow['MaxiStock']);
  delete normalizedRow['articulo_MiniStock'];
  delete normalizedRow['articulo_MaxiStock'];

  return row;
};

const ARTICULO_SCHEMA_CACHE_TTL = 5 * 60 * 1000;
const articuloSchemaCache = new Map<number, { ts: number; columns: Set<string> }>();

const getArticuloColumnSet = async (storeId: number): Promise<Set<string>> => {
  const cached = articuloSchemaCache.get(storeId);
  if (cached && Date.now() - cached.ts < ARTICULO_SCHEMA_CACHE_TTL) {
    return cached.columns;
  }

  const tableName = `store_${storeId}_articulo`;
  const rows = extractData(await executeQuery(
    `SELECT COLUMN_NAME
     FROM information_schema.columns
     WHERE table_schema = DATABASE()
       AND table_name = ?
       AND column_name IN ('NombreCN', 'NombreES', 'Descripcion')`,
    [tableName]
  ));

  const columns = new Set<string>(rows.map((r: any) => String(r.COLUMN_NAME || '')));
  articuloSchemaCache.set(storeId, { ts: Date.now(), columns });
  return columns;
};

const buildArticuloNameExpr = async (
  storeId: number,
  alias: string,
  articuloIdExpr: string
): Promise<{ productNameExpr: string; chineseNameExpr: string; spanishNameExpr: string }> => {
  const cols = await getArticuloColumnSet(storeId);
  const cnExpr = cols.has('NombreCN') ? `${alias}.NombreCN` : 'NULL';
  const esExpr = cols.has('NombreES')
    ? `${alias}.NombreES`
    : (cols.has('Descripcion') ? `${alias}.Descripcion` : 'NULL');

  const candidates: string[] = [];
  if (cols.has('NombreCN')) candidates.push(`NULLIF(${alias}.NombreCN, '')`);
  if (cols.has('NombreES')) {
    candidates.push(`NULLIF(${alias}.NombreES, '')`);
  } else if (cols.has('Descripcion')) {
    candidates.push(`NULLIF(${alias}.Descripcion, '')`);
  }
  candidates.push(`CONCAT('商品 ', ${articuloIdExpr})`);

  return {
    productNameExpr: `COALESCE(${candidates.join(', ')})`,
    chineseNameExpr: cnExpr,
    spanishNameExpr: esExpr
  };
};

const resolveValidStoreId = async (storeIdParam?: number): Promise<number> => {
  const normalizedStoreId =
    typeof storeIdParam === 'number' && Number.isFinite(storeIdParam) && storeIdParam > 0
      ? storeIdParam
      : undefined;

  if (normalizedStoreId) {
    return normalizedStoreId;
  }

  const fallbackStoreId = await getStoreId();
  if (!Number.isFinite(fallbackStoreId) || fallbackStoreId <= 0) {
    throw new Error('未找到有效的店铺ID');
  }
  return fallbackStoreId;
};

// 获取商品总数（用于仪表盘显示）
export const getTotalProductsCount = async (storeId?: number | 'all'): Promise<number> => {
  try {
    if (storeId && storeId !== 'all') {
      // 验证store_id是否存在且活跃
      const storeCheckQuery = `SELECT store_id FROM stores WHERE store_id = ? AND store_status = 'active' LIMIT 1`;
      const storeCheckResponse = await executeQuery(storeCheckQuery, [storeId]);
      const storeCheckResult = Array.isArray(storeCheckResponse) ? storeCheckResponse : (storeCheckResponse?.data || []);

      if (!storeCheckResult || storeCheckResult.length === 0) {
        console.error(`店铺ID ${storeId} 不存在或未激活`);
        throw new Error(`店铺ID ${storeId} 不存在或未激活`);
      }

      // 获取特定店铺的商品总数（以商品档案条数为准，不依赖库存表）
      const query = `SELECT COUNT(*) as total_products FROM store_${storeId}_articulo WHERE ArticuloID <> '0'`;
      const response = await executeQuery(query);

      const result = response?.data || response;
      if (!result || !Array.isArray(result) || result.length === 0 || !result[0]) {
        return 0;
      }

      return parseInt(String(result[0].total_products), 10) || 0;
    } else {
      // 获取所有店铺的商品总数
      const stores = await getAllAvailableStores();
      let totalCount = 0;

      for (const store of stores) {
        // 验证每个store_id是否存在且活跃
        const storeCheckQuery = `SELECT store_id FROM stores WHERE store_id = ? AND store_status = 'active' LIMIT 1`;
        const storeCheckResponse = await executeQuery(storeCheckQuery, [store.id]);
        const storeCheckResult = Array.isArray(storeCheckResponse) ? storeCheckResponse : (storeCheckResponse?.data || []);

        if (!storeCheckResult || storeCheckResult.length === 0) {
          continue;
        }

        const query = `SELECT COUNT(*) as total_products FROM store_${store.id}_articulo WHERE ArticuloID <> '0'`;
        const response = await executeQuery(query);
        const result = response?.data || response;

        if (!result || !Array.isArray(result) || result.length === 0 || !result[0]) {
          continue;
        }

        const storeCount = parseInt(String(result[0].total_products), 10) || 0;
        totalCount += storeCount;
      }

      return totalCount;
    }
  } catch (error) {
    console.error('获取商品总数失败:', error);
    return 0;
  }
};

// 获取所有库存信息（包含产品信息）
export const getAllStock = async (storeIdParam?: number) => {
  try {
    const storeId = storeIdParam ?? (await resolveValidStoreId());
    // 直接调用后端 overview REST 接口（单次请求，服务端一条 SQL 完成）
    const response = await api.get(`/stock/${storeId}/overview`);
    return (response.data?.data || []) as any[];
  } catch (error) {
    console.error('获取库存信息失败:', error);
    throw error;
  }
};

// 根据商品编码获取库存信息
export const getStockByArticuloID = async (articuloID: string, storeIdParam?: number) => {
  try {
    const storeId = await resolveValidStoreId(storeIdParam); // 用于表名
    const articuloNameExpr = await buildArticuloNameExpr(storeId, 'p', 's.ArticuloID');

    // 验证store_id是否存在且活跃
    const storeCheckQuery = `SELECT store_id FROM stores WHERE store_id = ? AND store_status = 'active' LIMIT 1`;
    const storeCheckResponse = await executeQuery(storeCheckQuery, [storeId]);
    const storeCheckResult = Array.isArray(storeCheckResponse) ? storeCheckResponse : (storeCheckResponse?.data || []);

    if (!storeCheckResult || storeCheckResult.length === 0) {
      console.error(`店铺ID ${storeId} 不存在或未激活`);
      throw new Error(`店铺ID ${storeId} 不存在或未激活`);
    }

    // 检测店铺专属库存表
    let stockTable = 'stock';
    try {
      const checkTableQuery = `SELECT COUNT(*) as count FROM information_schema.tables WHERE table_schema = DATABASE() AND table_name = ?`;
      const checkResponse = await executeQuery(checkTableQuery, [`store_${storeId}_stock`]);
      const result = Array.isArray(checkResponse) ? checkResponse : (checkResponse?.data || []);
      const rows = Array.isArray(result) ? result : (result?.data || []);
      if (rows.length > 0 && Number(rows[0]?.count) > 0) {
        stockTable = `store_${storeId}_stock`;
      }
    } catch (e) {
      stockWarn('检查店铺库存表是否存在时出错, fallback 到全局 stock 表', e);
    }

    const query = `
      SELECT s.*, ${articuloNameExpr.productNameExpr} as product_name, p.ArticuloID as product_code,
        p.ClaseID as category, p.PrecioDetalle as unit_price,
        p.PrecioCoste as purchase_price,
        p.CodigoBarra as barcode, ${articuloNameExpr.chineseNameExpr} as chinese_name,
        p.PrecioMayor as wholesale_price,
        p.MiniStock as articulo_MiniStock,
        p.MaxiStock as articulo_MaxiStock,
        img.image_url as image_url
      FROM ${stockTable} s
      LEFT JOIN store_${storeId}_articulo p ON s.ArticuloID = p.ArticuloID
      LEFT JOIN inventory_system.\`store_${storeId}_product_images\` img ON img.articulo_id = s.ArticuloID
      WHERE s.ArticuloID = ?
    `;
    const resultResponse = await executeQuery(query, [articuloID]);
    const result = Array.isArray(resultResponse) ? resultResponse : (resultResponse?.data || []);

    // 如果找到库存记录但没有关联产品，尝试在其他商店查找产品
    if (result.length > 0 && !result[0].product_name) {
      const alternativeStoreId = await findStoreWithProduct(articuloID, storeId);

      if (alternativeStoreId !== null) {
        const altExpr = await buildArticuloNameExpr(alternativeStoreId, 'p', 's.ArticuloID');
        // 在另一个商店找到了产品，重新查询结合这个商店的产品信息
        const alternativeQuery = `
          SELECT s.*, ${altExpr.productNameExpr} as product_name, p.ArticuloID as product_code,
            p.ClaseID as category, p.PrecioDetalle as unit_price,
            p.PrecioCoste as purchase_price,
            p.CodigoBarra as barcode, ${altExpr.chineseNameExpr} as chinese_name,
            p.PrecioMayor as wholesale_price,
            p.MiniStock as articulo_MiniStock,
            p.MaxiStock as articulo_MaxiStock,
            img.image_url as image_url
          FROM stock s
          LEFT JOIN store_${alternativeStoreId}_articulo p ON s.ArticuloID = p.ArticuloID
          LEFT JOIN inventory_system.\`store_${alternativeStoreId}_product_images\` img ON img.articulo_id = s.ArticuloID
          WHERE s.ArticuloID = ?
        `;
        const alternativeResultResponse = await executeQuery(alternativeQuery, [articuloID]);
        const alternativeResult = Array.isArray(alternativeResultResponse) ? alternativeResultResponse : (alternativeResultResponse?.data || []);

        if (alternativeResult.length > 0 && alternativeResult[0].product_name) {
          stockLog(`从其他商店(${alternativeStoreId})找到产品信息: ${articuloID}`);
          return applyArticuloStockLimits(alternativeResult[0]);
        }
      }
    }

    // 如果找到记录但没有关联产品，确保 product_code 字段至少有一个值
    if (result.length > 0 && !result[0].product_code) {
      result[0].product_code = result[0].ArticuloID;
      result[0].product_name = `未知商品 (${result[0].ArticuloID})`;
    }

    return applyArticuloStockLimits(result[0]);
  } catch (error) {
    console.error(`获取商品(编码: ${articuloID})库存信息失败:`, error);
    throw error;
  }
};

// 更新库存数量
export const updateStockQuantity = async (articuloID: string, quantity: number, storeIdParam?: number) => {
  try {
    stockLog(`更新库存数量: 商品编码=${articuloID}, 新数量=${quantity}, 店铺ID=${storeIdParam}`);

    // 首先获取当前库存信息
    const stockInfo = await getStockByArticuloID(articuloID, storeIdParam);

    if (!stockInfo) {
      console.error(`未找到商品 ${articuloID}`);
      throw new Error(`未找到商品编码为 ${articuloID} 的库存记录`);
    }

    // 优先从商品表获取库存上下限，找不到再回退到库存表的值
    let articuloMiniStock: number | null = null;
    let articuloMaxiStock: number | null = null;
    try {
      const storeId = storeIdParam || await getStoreId();
      const artResult = await executeQuery(
        `SELECT MiniStock, MaxiStock FROM store_${storeId}_articulo WHERE ArticuloID = ?`,
        [articuloID]
      );
      const artRows = Array.isArray(artResult) ? artResult : (artResult?.data || []);
      if (artRows.length > 0 && artRows[0]?.MiniStock != null) {
        articuloMiniStock = Number(artRows[0].MiniStock);
      }
      if (artRows.length > 0 && artRows[0]?.MaxiStock != null) {
        articuloMaxiStock = Number(artRows[0].MaxiStock);
      }
    } catch (e) { /* 回退到库存表的值 */ }

    // 确保库存上下限都是数字类型，并优先使用商品表的值
    const miniStock = normalizeStockLimit(articuloMiniStock, stockInfo.MiniStock, Number.NaN);
    const maxiStock = normalizeStockLimit(articuloMaxiStock, stockInfo.MaxiStock, Number.NaN);

    // 确保数量为数字
    const newQuantity = Number(quantity);
    if (isNaN(newQuantity)) {
      throw new Error('库存数量不是有效数字');
    }

    // 计算库存状态 - 新的逻辑
    let status = stockInfo.status || 0;

    // 库存不足且当前状态为0(正常)时，将状态改为1(警告)
    if (newQuantity <= miniStock && status === 0) {
      status = 1;
    }
    // 库存充足时，始终将状态改为0(正常)
    else if (newQuantity > miniStock) {
      status = 0;
    }
    // 其他情况保持当前状态不变
    // 使用/api/db/stock API端点更新库存
    const response = await api.post('/db/stock', {
      action: 'update',
      storeId: storeIdParam,
      data: {
        ArticuloID: articuloID,
        Stock: newQuantity,
        SitioEnAlmacen: stockInfo.SitioEnAlmacen || '',
        MiniStock: isNaN(miniStock) ? 10 : miniStock,
        MaxiStock: isNaN(maxiStock) ? 100 : maxiStock,
        status: status
      }
    });

    stockLog(`updateStockQuantity 响应:`, JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    console.error(`更新商品(编码: ${articuloID})库存数量失败:`, error);
    throw error;
  }
};

// 创建新库存记录
export const createStock = async (stock: Omit<Stock, 'product_name' | 'product_code' | 'category' | 'unit_price' | 'barcode' | 'chinese_name' | 'wholesale_price'> & { importMode?: 'replace' | 'add' }) => {
  try {
    // 首先检查是否已存在该商品的库存记录
    const existingQuery = `
      SELECT * FROM stock
      WHERE ArticuloID = ?
    `;
    const existingResponse = await executeQuery(existingQuery, [stock.ArticuloID]);
    const existing = Array.isArray(existingResponse) ? existingResponse : (existingResponse?.data || []);

    // 计算默认状态 - 新的逻辑
    // 对于新记录，如果库存小于最小库存，默认为警告状态(1)，否则为正常状态(0)
    const defaultStatus = stock.Stock <= stock.MiniStock ? 1 : 0;

    // 优先使用提供的status值，如果不存在则使用计算值
    const status = stock.status !== undefined ? stock.status : defaultStatus;

    if (existing && existing.length > 0) {
      // 如果是累加模式，计算新的库存数量
      let newStock = stock.Stock;

      if (stock.importMode === 'add') {
        const existingStock = typeof existing[0].Stock === 'string' ?
          parseFloat(existing[0].Stock) : Number(existing[0].Stock || 0);
        newStock = existingStock + Number(stock.Stock);
        // stockLog(`累加模式: 原库存=${existingStock}, 新增=${stock.Stock}, 累加后=${newStock}`);
      }

      // 如果库存记录已存在，通过新API更新它
      const response = await api.post('/db/stock', {
        action: 'update',
        data: {
          Stock: newStock,
          SitioEnAlmacen: stock.SitioEnAlmacen || '',
          MiniStock: stock.MiniStock || 10,
          MaxiStock: stock.MaxiStock || 100,
          ArticuloID: stock.ArticuloID,
          status: status
        }
      });

      return {
        ...response.data,
        message: '库存记录已更新',
        updated: true
      };
    } else {
      // 创建新的库存记录，通过新API
      const response = await api.post('/db/stock', {
        action: 'insert',
        data: {
          ArticuloID: stock.ArticuloID,
          Stock: stock.Stock,
          SitioEnAlmacen: stock.SitioEnAlmacen || '',
          MiniStock: stock.MiniStock || 10,
          MaxiStock: stock.MaxiStock || 100,
          status: status
        }
      });

      return {
        ...response.data,
        message: '库存记录已创建',
        created: true
      };
    }
  } catch (error) {
    console.error('创建库存记录失败:', error);
    throw error;
  }
};

// 调整库存数量（用于增加或减少库存）
export const adjustStockQuantity = async (articuloID: string, adjustment: number, storeIdParam?: number) => {
  try {
    stockLog(`调整库存: 商品编码=${articuloID}, 调整量=${adjustment}, 店铺ID=${storeIdParam}`);

    // 首先获取当前库存信息
    const stockInfo = await getStockByArticuloID(articuloID, storeIdParam);

    if (!stockInfo) {
      console.error(`未找到商品 ${articuloID}`);
      throw new Error(`未找到商品编码为 ${articuloID} 的库存记录`);
    }

    // 计算新的库存数量（当前库存 + 调整量）
    const currentStock = typeof stockInfo.Stock === 'string' ?
      parseFloat(stockInfo.Stock) : Number(stockInfo.Stock || 0);
    const newQuantity = Math.max(0, currentStock + adjustment); // 确保库存不小于0

    // 使用现有的updateStockQuantity函数更新库存
    return await updateStockQuantity(articuloID, newQuantity, storeIdParam);
  } catch (error) {
    console.error(`调整商品(编码: ${articuloID})库存数量失败:`, error);
    throw error;
  }
};

// 批量导入库存数据（用于从Excel导入）
export const importStock = async (stockData: (Partial<Stock> & { importMode?: 'replace' | 'add' })[]) => {
  try {
    stockLog(`开始批量导入库存数据，共 ${stockData.length} 条记录，导入模式: ${stockData[0]?.importMode || 'replace'}`);

    const results = [];
    let successCount = 0;
    let failCount = 0;

    // 逐条处理库存数据
    for (const item of stockData) {
      try {
        // 确认ArticuloID存在
        if (!item.ArticuloID) {
          console.error('缺少必要的商品编码(ArticuloID)');
          failCount++;
          continue;
        }

        // 创建或更新库存记录
        const result = await createStock({
          ArticuloID: item.ArticuloID,
          Stock: Number(item.Stock || 0),
          SitioEnAlmacen: item.SitioEnAlmacen || '',
          MiniStock: Number(item.MiniStock || 10),
          MaxiStock: Number(item.MaxiStock || 100),
          status: item.status,
          importMode: item.importMode // 传递导入模式
        });

        results.push(result);
        successCount++;
      } catch (error) {
        console.error(`导入商品(编码: ${item.ArticuloID})数据失败:`, error);
        failCount++;
      }
    }

    stockLog(`库存导入完成: 成功 ${successCount} 条, 失败 ${failCount} 条`);
    return {
      success: successCount,
      failed: failCount,
      results: results
    };
  } catch (error) {
    console.error('批量导入库存数据失败:', error);
    throw error;
  }
};

// Low stock count for dashboard statistics.
export const getLowStockCount = async (storeIdParam?: number | 'all'): Promise<number> => {
  try {
    const { total } = await getLowStockItemsPage(storeIdParam, { limit: 1, offset: 0 });
    return total;
  } catch (error) {
    console.error('getLowStockCount error', error);
    return 0;
  }
};

export const getLowStockItems = async (storeIdParam?: number | 'all', limit?: number) => {
  try {
    // 先更新所有库存状态 - 暂时禁用，因为后端API需要店铺ID参数
    // TODO: 修复 updateAllStockStatus 以支持多店铺或传递店铺ID
    // await updateAllStockStatus();

    // 如果是获取所有店铺的数据
    if (storeIdParam === undefined || storeIdParam === 'all') {
      return await getAllStoresLowStockItems();
    }

    // 获取特定店铺的数据
    const storeId = storeIdParam;

    // 验证store_id是否存在且活跃
    const storeCheckQuery = `SELECT store_id FROM stores WHERE store_id = ? AND store_status = 'active' LIMIT 1`;
    const storeCheckResponse = await executeQuery(storeCheckQuery, [storeId]);
    const storeCheckResult = Array.isArray(storeCheckResponse) ? storeCheckResponse : (storeCheckResponse?.data || []);

    if (!storeCheckResult || storeCheckResult.length === 0) {
      console.error(`店铺ID ${storeId} 不存在或未激活`);
      throw new Error(`店铺ID ${storeId} 不存在或未激活`);
    }

    // 检测专属库存表
    let stockTable = 'stock';
    try {
      const result = await executeQuery(
        'SELECT COUNT(*) as count FROM information_schema.tables WHERE table_schema = DATABASE() AND table_name = ?',
        [`store_${storeId}_stock`]
      );
      const rows = Array.isArray(result) ? result : (result?.data || []);
      if (rows.length > 0 && Number(rows[0]?.count) > 0) {
        stockTable = `store_${storeId}_stock`;
      }
    } catch (e) {
      stockWarn('检查店铺库存表时出错, 使用全局 stock', e);
    }

    // 首先检查表是否存在
    const tableName = `store_${storeId}_articulo`;
    const checkTableQuery = `
      SELECT COUNT(*) as count 
      FROM information_schema.tables 
      WHERE table_schema = DATABASE() 
      AND table_name = ?
    `;

    // stockLog(`检查表 ${tableName} 是否存在...`);
    let tableExists = true;
    try {
      const result = await executeQuery(checkTableQuery, [tableName]);
      const rows = Array.isArray(result) ? result : (result?.data || []);
      tableExists = rows.length > 0 && Number(rows[0]?.count) > 0;
    } catch (error) {
      console.error(`检查表 ${tableName} 是否存在时出错`, error);
      tableExists = false;
    }

    let query;
    if (tableExists) {
      const articuloNameExpr = await buildArticuloNameExpr(storeId, 'p', 's.ArticuloID');
      // 判断 stock 表里是否含有 status 列（旧店铺表可能没有）
      let hasStatusColumn = false;
      try {
        const result = await executeQuery(
          'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
          [stockTable, 'status']
        );
        const rows = Array.isArray(result) ? result : (result?.data || []);
        const statusColCount = rows.length > 0 ? rows[0]?.count : 0;
        hasStatusColumn = Number(statusColCount) > 0;
      } catch (e) {
        stockWarn('检查 status 列是否存在时出错', e);
      }

      // 如果没有 status 列，继续检查是否存在 Stock 和 MiniStock 列，否则放弃查询
      if (!hasStatusColumn) {
        let hasStockColumns = false;
        try {
          const checkResult = await executeQuery(
            `SELECT COUNT(*) as cnt FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name IN ('Stock','MiniStock')`,
            [stockTable]
          );
          const checkRows = Array.isArray(checkResult) ? checkResult : (checkResult?.data || []);
          hasStockColumns = checkRows.length > 0 && Number(checkRows[0]?.cnt) >= 2;
        } catch (e) { /* ignore */ }

        if (!hasStockColumns) {
          stockWarn(`表 ${stockTable} 缺少 Stock/MiniStock 列，跳过低库存查询`);
          return [];
        }
      }

      // 检查 proveedor 表是否有 NombreCN 字段
      const proveedorTableLowStock = `store_${storeId}_proveedor`;
      let supplierNameFieldLowStock = 'NULL';
      try {
        const checkProveedorTableQuery = `SELECT COUNT(*) as count FROM information_schema.tables WHERE table_schema = DATABASE() AND table_name = ?`;
        const provResult = await executeQuery(checkProveedorTableQuery, [proveedorTableLowStock]);
        const provRows = Array.isArray(provResult) ? provResult : (provResult?.data || []);
        const proveedorTableExists = provRows.length > 0 ? provRows[0] : null;
        if (proveedorTableExists && Number(proveedorTableExists.count) > 0) {
          // 检查 NombreCN 字段是否存在
          const colResult = await executeQuery(
            'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
            [proveedorTableLowStock, 'NombreCN']
          );
          const colRows = Array.isArray(colResult) ? colResult : (colResult?.data || []);
          const columnCheck = colRows.length > 0 ? colRows[0] : null;
          if (columnCheck && Number(columnCheck.count) > 0) {
            supplierNameFieldLowStock = 'pr.NombreCN';
          } else {
            // 尝试使用 Nombre 字段作为后备
            const nombreResult = await executeQuery(
              'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
              [proveedorTableLowStock, 'Nombre']
            );
            const nombreRows = Array.isArray(nombreResult) ? nombreResult : (nombreResult?.data || []);
            const nombreCheck = nombreRows.length > 0 ? nombreRows[0] : null;
            if (nombreCheck && Number(nombreCheck.count) > 0) {
              supplierNameFieldLowStock = 'pr.Nombre';
            } else {
              // 如果都没有，使用 NombreES 作为最后的后备
              const nombreESResult = await executeQuery(
                'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
                [proveedorTableLowStock, 'NombreES']
              );
              const nombreESRows = Array.isArray(nombreESResult) ? nombreESResult : (nombreESResult?.data || []);
              const nombreESCheck = nombreESRows.length > 0 ? nombreESRows[0] : null;
              if (nombreESCheck && Number(nombreESCheck.count) > 0) {
                supplierNameFieldLowStock = 'pr.NombreES';
              } else {
                supplierNameFieldLowStock = 'NULL';
              }
            }
          }
        }
      } catch (e) {
        stockWarn(`检查店铺 ${storeId} 的 proveedor 表字段时出错:`, e);
        supplierNameFieldLowStock = 'NULL';
      }

      // 根据是否存在 status 列构建 WHERE 条件
      // 使用商品表(p)的 MiniStock 作为库存下限，找不到则回退到库存表(s)的 MiniStock
      const whereClause = 's.Stock <= p.MiniStock';

      query = `
        SELECT s.*,
               ${articuloNameExpr.productNameExpr} as product_name,
               p.ArticuloID as product_code,
               p.ClaseID as category,
               p.PrecioDetalle as unit_price,
               p.CodigoBarra as barcode,
               ${articuloNameExpr.chineseNameExpr} as chinese_name,
               p.PrecioMayor as wholesale_price,
               p.ProveedorID as supplier_id,
               ${supplierNameFieldLowStock} as supplier_name,
               img.image_url as image_url
        FROM ${stockTable} s
        LEFT JOIN store_${storeId}_articulo p ON s.ArticuloID = p.ArticuloID
        LEFT JOIN store_${storeId}_proveedor pr ON p.ProveedorID = pr.ProveedorID
        LEFT JOIN inventory_system.\`store_${storeId}_product_images\` img ON img.articulo_id = s.ArticuloID
        WHERE ${whereClause}
      `;
    } else {
      // 如果表不存在，只查询stock表
      stockWarn(`店铺 ${storeId} 未创建库存表，返回空低库存列表`);
      return [];
    }

    const result = await executeQuery(query);

    const resultArray = Array.isArray(result) ? result : (result?.data || []);
    return resultArray;
  } catch (error) {
    console.error('获取低库存商品失败', error);
    throw error;
  }
};

export const getLowStockItemsPage = async (
  storeIdParam?: number | 'all',
  options: { limit?: number; offset?: number; supplierIds?: string[]; search?: string } = {}
) => {
  const { limit = 10, offset = 0, supplierIds = [], search = '' } = options;
  const params: Record<string, any> = {
    limit,
    offset,
  };

  if (supplierIds.length > 0) {
    params.supplierIds = supplierIds.join(',');
  }

  if (String(search).trim()) {
    params.search = String(search).trim();
  }

  try {
    if (storeIdParam === undefined || storeIdParam === 'all') {
      const response = await api.get('/global-stock/low-stock', { params });
      const payload = response.data || {};
      const items = Array.isArray(payload.data) ? payload.data : [];
      const totalValue = Number(payload.total);
      return {
        items,
        total: Number.isFinite(totalValue) ? totalValue : items.length,
      };
    }

    const response = await api.get(`/dashboard/low-stock/${storeIdParam}`, { params });
    const payload = response.data || {};
    const items = Array.isArray(payload.data) ? payload.data : [];
    const totalValue = Number(payload.total);
    return {
      items,
      total: Number.isFinite(totalValue) ? totalValue : items.length,
    };
  } catch (error) {
    console.error('获取低库存商品分页数据失败', error);
    throw error;
  }
};


// 批量禁用商品（设置 Bloqueado=1，不再显示于低库存预警）
export const batchBlockProducts = async (storeId: number, productIds: string[]): Promise<{ affected: number }> => {
  const res = await api.put(`/${storeId}/products/batch-block`, { productIds });
  return res.data;
};

export const updateAllStockStatus = async () => {
  try {
    stockLog('开始更新所有库存状态...');

    // 使用db接口而不是直接执行SQL
    const response = await api.post('/db/update-stock-status', {
      action: 'update-all'
    });

    stockLog('库存状态更新完成', response.data);
    return response.data;
  } catch (error) {
    console.error('更新所有库存状态失败', error);
    throw error;
  }
};

// 删除库存记录
export const deleteStockItem = async (articuloId: string | number, storeId?: number) => {
  try {
    const currentStoreId = await resolveValidStoreId(storeId);

    // 验证store_id是否存在且活跃
    const storeCheckQuery = `SELECT store_id FROM stores WHERE store_id = ? AND store_status = 'active' LIMIT 1`;
    const storeCheckResponse = await executeQuery(storeCheckQuery, [currentStoreId]);
    const storeCheckResult = Array.isArray(storeCheckResponse) ? storeCheckResponse : (storeCheckResponse?.data || []);

    if (!storeCheckResult || storeCheckResult.length === 0) {
      console.error(`店铺ID ${currentStoreId} 不存在或未激活`);
      throw new Error(`店铺ID ${currentStoreId} 不存在或未激活`);
    }

    // 检查是否存在店铺专属库存表
    let stockTable = 'stock';
    try {
      const checkTableQuery = `SELECT COUNT(*) as count FROM information_schema.tables WHERE table_schema = DATABASE() AND table_name = ?`;
      const checkResponse = await executeQuery(checkTableQuery, [`store_${currentStoreId}_stock`]);
      const checkResult = checkResponse?.data || checkResponse;
      const firstRow = Array.isArray(checkResult) && checkResult.length > 0 ? checkResult[0] : null;
      if (firstRow && Number(firstRow.count) > 0) {
        stockTable = `store_${currentStoreId}_stock`;
      }
    } catch (e) {
      stockWarn('检查店铺库存表是否存在时出错, fallback 到全局 stock 表', e);
    }

    // 删除库存记录
    const deleteQuery = `DELETE FROM ${stockTable} WHERE ArticuloID = ?`;
    const deleteResponse = await executeQuery(deleteQuery, [articuloId]);

    // Handle both array and object response formats
    const affectedRows = (deleteResponse && typeof deleteResponse === 'object' && 'affectedRows' in deleteResponse)
      ? deleteResponse.affectedRows
      : 0;

    stockLog(`删除库存记录成功: ArticuloID=${articuloId}, 影响行数=${affectedRows}`);

    return {
      success: true,
      affectedRows: affectedRows,
      message: affectedRows > 0 ? '库存记录删除成功' : '未找到要删除的库存记录'
    };
  } catch (error) {
    console.error('删除库存记录失败:', error);
    throw error;
  }
};

// 获取所有店铺的低库存商品
const getAllStoresLowStockItems = async () => {
  try {
    // 从实际存在的表结构获取所有店铺列表
    const stores = await getAllAvailableStores();

    let allLowStockItems: any[] = [];

    for (const store of stores) {
      try {
        const storeId = store.id;
        const stockTable = `store_${storeId}_stock`;
        const articuloTable = `store_${storeId}_articulo`;
        const proveedorTable = `store_${storeId}_proveedor`;

        // 检查表是否存在
        const checkTableQuery = `
          SELECT COUNT(*) as count
          FROM information_schema.tables
          WHERE table_schema = DATABASE()
          AND table_name = ?
        `;

        const stockTableResponse = await executeQuery(checkTableQuery, [stockTable]);
        const stockTableResult = stockTableResponse?.data || stockTableResponse;
        const [stockTableExists] = Array.isArray(stockTableResult) ? stockTableResult : [];

        const articuloTableResponse = await executeQuery(checkTableQuery, [articuloTable]);
        const articuloTableResult = articuloTableResponse?.data || articuloTableResponse;
        const [articuloTableExists] = Array.isArray(articuloTableResult) ? articuloTableResult : [];

        if (!stockTableExists?.count || !articuloTableExists?.count) {
          stockWarn(`店铺 ${storeId} 的表不存在，跳过`);
          continue;
        }

        // 检查 proveedor 表是否有 NombreCN 字段
        let supplierNameField = 'NULL';
        try {
          const proveedorTableResponse = await executeQuery(checkTableQuery, [proveedorTable]);
          const proveedorTableResult = proveedorTableResponse?.data || proveedorTableResponse;
          const [proveedorTableExists] = Array.isArray(proveedorTableResult) ? proveedorTableResult : [];
          if (proveedorTableExists?.count > 0) {
            // 检查 NombreCN 字段是否存在
            const columnCheckResponse = await executeQuery(
              'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
              [proveedorTable, 'NombreCN']
            );
            const columnCheckResult = columnCheckResponse?.data || columnCheckResponse;
            const [columnCheck] = Array.isArray(columnCheckResult) ? columnCheckResult : [];
            if (columnCheck?.count > 0) {
              supplierNameField = 'pr.NombreCN';
            } else {
              // 尝试使用 Nombre 字段作为后备
              const nombreCheckResponse = await executeQuery(
                'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
                [proveedorTable, 'Nombre']
              );
              const nombreCheckResult = nombreCheckResponse?.data || nombreCheckResponse;
              const [nombreCheck] = Array.isArray(nombreCheckResult) ? nombreCheckResult : [];
              if (nombreCheck?.count > 0) {
                supplierNameField = 'pr.Nombre';
              } else {
                // 如果都没有，使用 NombreES 作为最后的后备
                const nombreESCheckResponse = await executeQuery(
                  'SELECT COUNT(*) as count FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? AND column_name = ?',
                  [proveedorTable, 'NombreES']
                );
                const nombreESCheckResult = nombreESCheckResponse?.data || nombreESCheckResponse;
                const [nombreESCheck] = Array.isArray(nombreESCheckResult) ? nombreESCheckResult : [];
                if (nombreESCheck?.count > 0) {
                  supplierNameField = 'pr.NombreES';
                } else {
                  supplierNameField = 'NULL';
                }
              }
            }
          }
        } catch (e) {
          stockWarn(`检查店铺 ${storeId} 的 proveedor 表字段时出错:`, e);
          supplierNameField = 'NULL';
        }

        // 查询该店铺的低库存商品
        const articuloNameExpr = await buildArticuloNameExpr(storeId, 'p', 's.ArticuloID');
        const query = `
          SELECT s.*,
                 ${articuloNameExpr.productNameExpr} as product_name,
                 p.ArticuloID as product_code,
                 p.ClaseID as category,
                 p.PrecioDetalle as unit_price,
                 p.CodigoBarra as barcode,
                 ${articuloNameExpr.chineseNameExpr} as chinese_name,
                 p.PrecioMayor as wholesale_price,
                 p.ProveedorID as supplier_id,
                 ${supplierNameField} as supplier_name,
                 ${storeId} as store_id,
                 '${store.name}' as store_name
          FROM ${stockTable} s
          LEFT JOIN ${articuloTable} p ON s.ArticuloID = p.ArticuloID
          LEFT JOIN ${proveedorTable} pr ON p.ProveedorID = pr.ProveedorID
          WHERE s.Stock <= p.MiniStock AND s.ArticuloID != 0
          ORDER BY s.ArticuloID
        `;

        const storeItemsResponse = await executeQuery(query);
        const storeItems = storeItemsResponse?.data || storeItemsResponse || [];
        if (Array.isArray(storeItems)) {
          allLowStockItems = allLowStockItems.concat(storeItems);
        }

      } catch (error) {
        console.error(`获取店铺 ${store.id} 的低库存数据失败:`, error);
      }
    }

    return allLowStockItems;
  } catch (error) {
    console.error('获取所有店铺低库存数据失败:', error);
    throw error;
  }
};
